import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table'
import clsx from 'clsx'
import { CircularProgress, makeStyles } from '@material-ui/core'
import Icon from '../../atoms/Icon'
import { useFormattingContext } from '../FormattingProvider/FormattingContext'

const DEPTH_BACKGROUNDS = [
  'var(--opal-surface-default, #fff)',
  'var(--color-neutral-50, #fafafa)',
  'var(--color-neutral-100, #f5f5f5)',
  'var(--color-neutral-150, #efefef)',
  'var(--color-neutral-200, #eaeaea)',
  'var(--color-neutral-200, #eaeaea)'
]

const useStyles = makeStyles(() => ({
  root: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative'
  },
  scroll: {
    flex: 1,
    overflow: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
    tableLayout: 'fixed'
  },
  thead: {
    position: 'sticky',
    top: 0,
    zIndex: 2
  },
  th: {
    padding: '6px 10px',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    color: 'var(--color-neutral-700, #555)',
    background: 'var(--opal-surface-default, #fff)',
    borderBottom: '2px solid var(--opal-border-default, #ddd)',
    whiteSpace: 'nowrap',
    userSelect: 'none'
  },
  thSortable: {
    cursor: 'pointer',
    '&:hover': { color: 'var(--color-neutral-900, #222)' }
  },
  thSticky: {
    position: 'sticky',
    left: 0,
    zIndex: 3,
    background: 'var(--opal-surface-default, #fff)'
  },
  thLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4
  },
  td: {
    padding: '4px 10px',
    height: 30,
    borderBottom: '1px solid var(--opal-border-default, #eee)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  tdSticky: {
    position: 'sticky',
    left: 0,
    zIndex: 1,
    background: 'inherit'
  },
  tdNegative: {
    color: 'var(--color-danger-500, #d32f2f)'
  },
  tdPositive: {
    color: 'var(--color-success-700, #2e7d32)'
  },
  tdRight: {
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums'
  },
  parentRow: {
    '& td': { fontWeight: 600 }
  },
  row: {
    '&:hover td': {
      background: 'var(--color-primary-50, rgba(0, 100, 255, 0.04))'
    }
  },
  dash: {
    color: 'var(--color-neutral-400, #aaa)'
  },
  nameCell: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    minWidth: 0
  },
  nameCellClickable: {
    cursor: 'pointer'
  },
  expandIcon: {
    flexShrink: 0,
    transition: 'transform 150ms ease'
  },
  expandIconExpanded: {
    transform: 'rotate(90deg)'
  },
  expandSpacer: {
    display: 'inline-block',
    width: 14,
    flexShrink: 0
  },
  nameText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  }
}))

function buildColumns (columnDefinitions, formatter, classes, onExpandRow) {
  const nameColumn = {
    id: '__name',
    accessorKey: 'name',
    header: 'Name',
    size: 280,
    minSize: 160,
    enableSorting: true,
    cell: ({ row, getValue }) => {
      const canExpand = row.getCanExpand()
      const depth = row.depth
      const isFetchingChildren = row.original?._subRowsFetching
      const needsLazyLoad = row.original?._childrenLoaded === false

      const handleClick = canExpand ? (e) => {
        if (needsLazyLoad && !isFetchingChildren) {
          onExpandRow(row.original)
          const toggle = row.getToggleExpandedHandler()
          if (!row.getIsExpanded()) {
            if (document.startViewTransition) {
              document.startViewTransition(() => toggle(e))
            } else {
              toggle(e)
            }
          }
        } else if (!isFetchingChildren) {
          if (document.startViewTransition) {
            document.startViewTransition(() => row.getToggleExpandedHandler()(e))
          } else {
            row.getToggleExpandedHandler()(e)
          }
        }
      } : undefined

      return (
        <div
          className={clsx(classes.nameCell, { [classes.nameCellClickable]: canExpand })}
          style={{ paddingLeft: depth * 20 + 8 }}
          onClick={handleClick}
        >
          {canExpand ? (
            isFetchingChildren
              ? <CircularProgress size={12} style={{ marginRight: 2, flexShrink: 0 }} />
              : (
                <Icon
                  additionalClasses={clsx(classes.expandIcon, { [classes.expandIconExpanded]: row.getIsExpanded() })}
                  name='chevronRight'
                  customSize={14}
                />
              )
          ) : (
            <span className={classes.expandSpacer} />
          )}
          <span className={classes.nameText}>{getValue()}</span>
        </div>
      )
    }
  }

  const dataColumns = columnDefinitions.map(col => ({
    id: col.accessorKey,
    accessorKey: col.accessorKey,
    header: col.header,
    size: 130,
    minSize: 90,
    enableSorting: true,
    meta: { format: col.format, align: col.align },
    cell: ({ getValue }) => {
      const value = getValue()
      if (value == null) return <span className={classes.dash}>--</span>
      return formatter(value, col.format)
    }
  }))

  return [nameColumn, ...dataColumns]
}

export default function PortfolioExplorerGrid ({ rows, columnDefinitions, onExpandRow }) {
  const classes = useStyles()
  const { formatter } = useFormattingContext()
  const [sorting, setSorting] = useState([])
  const [expanded, setExpanded] = useState({})

  const handleExpandRow = useCallback((row) => {
    if (onExpandRow) onExpandRow(row)
  }, [onExpandRow])

  const columns = useMemo(
    () => buildColumns(columnDefinitions, formatter, classes, handleExpandRow),
    [columnDefinitions, formatter, classes, handleExpandRow]
  )

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, expanded },
    onSortingChange: setSorting,
    onExpandedChange: setExpanded,
    getSubRows: row => row._placeholder ? undefined : row.data,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel()
  })

  return (
    <div className={classes.root}>
      <div className={classes.scroll}>
        <table className={classes.table}>
          <thead className={classes.thead}>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  const meta = header.column.columnDef.meta
                  const isRight = meta?.align === 'right' || header.column.id !== '__name'
                  return (
                    <th
                      key={header.id}
                      className={clsx(classes.th, {
                        [classes.thSticky]: header.column.id === '__name',
                        [classes.thSortable]: header.column.getCanSort()
                      })}
                      style={{
                        width: header.column.getSize(),
                        textAlign: isRight && header.column.id !== '__name' ? 'right' : 'left'
                      }}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <span className={classes.thLabel}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getIsSorted() === 'asc' && <Icon name='up' customSize={12} />}
                        {header.column.getIsSorted() === 'desc' && <Icon name='down' customSize={12} />}
                      </span>
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map(row => {
              if (row.original?._placeholder) return null
              const depthBg = DEPTH_BACKGROUNDS[Math.min(row.depth, 5)]
              return (
                <tr
                  key={row.id}
                  className={clsx(classes.row, { [classes.parentRow]: row.getCanExpand() })}
                  style={{ background: depthBg }}
                >
                  {row.getVisibleCells().map(cell => {
                    const meta = cell.column.columnDef.meta
                    const isRight = meta?.align === 'right' || cell.column.id !== '__name'
                    const rawValue = cell.getValue()
                    const isNegative = typeof rawValue === 'number' && rawValue < 0
                    const isReturns = meta?.format === 'returns'
                    return (
                      <td
                        key={cell.id}
                        className={clsx(classes.td, {
                          [classes.tdSticky]: cell.column.id === '__name',
                          [classes.tdRight]: isRight && cell.column.id !== '__name',
                          [classes.tdNegative]: isNegative,
                          [classes.tdPositive]: isReturns && typeof rawValue === 'number' && rawValue > 0
                        })}
                        style={{ width: cell.column.getSize() }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

PortfolioExplorerGrid.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  columnDefinitions: PropTypes.arrayOf(PropTypes.shape({
    accessorKey: PropTypes.string,
    header: PropTypes.string,
    format: PropTypes.string,
    align: PropTypes.string
  })).isRequired,
  onExpandRow: PropTypes.func
}
